import React, { useEffect, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { Col, Row } from "react-bootstrap";
import { FaUserEdit } from "react-icons/fa";
import { FaCircleXmark } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import {
  AddButton,
  Button,
  Input,
  SubTitle,
} from "../../Components/CustomComponents";
import DeleteModal from "../../Components/Modals/DeleteModal";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import Toast from "../../Hooks/useToast";
import useFaqController from "../../controllers/FaqControllers/useFaqController";
import modalStyles from "../../Components/Modals/styles/editModal.module.scss";
import styles from "./styles/FaqManagement.module.scss";

function clipText(text, max = 120) {
  if (text == null || text === "") return "-";
  const s = String(text);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export default function FaqManagement() {
  const {
    values: { list: rowData, loading, saving },
    functions: { handleCreateFaq, handleUpdateFaq, handleDeleteFaq },
  } = useFaqController();

  const [rowDataOptimistic, setRowDataOptimistic] = useState([]);
  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [editedData, setEditedData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");

  useEffect(() => {
    if (loading) {
      setIndicationMessage("loading...");
      return;
    }
    setIndicationMessage(rowData.length ? "" : "No FAQs found");
  }, [loading, rowData.length]);

  useEffect(() => {
    setRowDataOptimistic(rowData);
  }, [rowData]);

  const handleDeleteModalOpen = () => setDeleteModal(true);
  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setEditedData(null);
  };

  const handleEditModalClose = () => {
    setEditModal(false);
    setEditedData(null);
    setQuestion("");
    setAnswer("");
  };

  const handleAddModalOpen = () => {
    setQuestion("");
    setAnswer("");
    setAddModal(true);
  };

  const handleAddModalClose = () => {
    setAddModal(false);
    setQuestion("");
    setAnswer("");
  };

  const onConfirmDelete = async (id) => {
    try {
      await handleDeleteFaq(id);
      handleDeleteModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  const openEdit = (row) => {
    setEditedData(row);
    setQuestion(row.question ?? "");
    setAnswer(row.answer ?? "");
    setEditModal(true);
  };

  const handleSaveAdd = async () => {
    if (!question.trim() || !answer.trim()) {
      Toast("Please enter both question and answer", "error");
      return;
    }
    try {
      await handleCreateFaq({
        question: question.trim(),
        answer: answer.trim(),
      });
      handleAddModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  const handleSaveEdit = async () => {
    if (!editedData?.id) return;
    if (!question.trim() || !answer.trim()) {
      Toast("Please enter both question and answer", "error");
      return;
    }
    try {
      await handleUpdateFaq(editedData.id, {
        question: question.trim(),
        answer: answer.trim(),
      });
      handleEditModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  function indication() {
    return indicationMessage;
  }

  const displayRows = rowDataOptimistic;

  const columns = [
    {
      dataField: "id",
      text: "ID",
      sort: true,
      headerStyle: { width: "80px" },
    },
    {
      dataField: "question",
      text: "Question",
      sort: true,
      headerStyle: { minWidth: "200px" },
      formatter: (cell, row) => (
        <span className={styles.cellClamp} title={row.question}>
          {clipText(row.question ?? cell, 200)}
        </span>
      ),
    },
    {
      dataField: "answer",
      text: "Answer",
      sort: true,
      headerStyle: { minWidth: "200px" },
      formatter: (cell, row) => (
        <span className={styles.cellClamp} title={row.answer}>
          {clipText(row.answer ?? cell, 200)}
        </span>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "220px" },
      formatter: (_cell, row) => {
        if (row.id == null) return null;
        return (
          <div className="d-flex justify-content-center align-items-center">
            <div
              className={`${styles.btnBg} d-flex justify-content-center align-items-center rounded-3 mx-2 p-2`}
              role="button"
              onClick={() => openEdit(row)}
            >
              <span className="align-items-center d-flex justify-content-center pe-1">
                <FaUserEdit />
              </span>
              <span>Edit</span>
            </div>
            <div
              className="d-flex justify-content-center align-items-center bg-danger rounded-3 p-2 mx-2"
              role="button"
              onClick={() => {
                setEditedData({
                  ...row,
                  name: row.question || "this FAQ",
                });
                handleDeleteModalOpen();
              }}
            >
              <span className="d-flex align-items-center pe-1">
                <MdDeleteForever />
              </span>
              <span>Delete</span>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.MainContainer}>
      {deleteModal ? (
        <DeleteModal
          handleDeleteModalClose={handleDeleteModalClose}
          data={editedData}
          onDelete={onConfirmDelete}
        />
      ) : null}

      {editModal ? (
        <ModalContainer handleClose={handleEditModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Edit FAQ</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleEditModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>
            <div className="w-100">
              <Row>
                <Col md={12}>
                  <p className="m-0">Question</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="text"
                    placeholder="Question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <p className="m-0">Answer</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="text"
                    as="textarea"
                    rows={4}
                    placeholder="Answer"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Button
                    classes="mt-2"
                    text={saving ? "Saving..." : "Update FAQ"}
                    onClick={handleSaveEdit}
                    disabled={saving}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </ModalContainer>
      ) : null}

      {addModal ? (
        <ModalContainer handleClose={handleAddModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Add FAQ</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleAddModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>
            <div className="w-100">
              <Row>
                <Col md={12}>
                  <p className="m-0">Question</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="text"
                    placeholder="Question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <p className="m-0">Answer</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="text"
                    as="textarea"
                    rows={4}
                    placeholder="Answer"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Button
                    classes="mt-2"
                    text={saving ? "Saving..." : "Save FAQ"}
                    onClick={handleSaveAdd}
                    disabled={saving}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </ModalContainer>
      ) : null}

      <div className={styles.headerRow}>
        <SubTitle text="FAQ Management" />
        <AddButton text="Add FAQ" onClick={handleAddModalOpen} />
      </div>

      <div className={styles.mainTable}>
        <Table data={displayRows} columns={columns} indication={indication} />
      </div>
    </div>
  );
}
